import { Button, ButtonGroup } from "@progress/kendo-react-buttons";
import { formatNumber } from "@progress/kendo-intl";
import { Grid, GridColumn as Column, GridNoRecords, GridToolbar } from "@progress/kendo-react-grid";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { QuoteHistoryViewModel } from "TypeGen/Customer/QuoteHistory/quote-history-view-model";
import { ViewModel } from "TypeGen/Customer/QuoteHistory/view-model";
import { fetchApi } from "../../services/api";
import { Title } from '../../utils/title';
import { CustomerNavBar } from "./NavBar";
import QuoteHistoryDetail from "./QuoteHistoryDetail";

type RouteComponentParams = {
  customerId: string;
};

const CustomerQuoteHistory = () => {
  const { customerId: customerIdParam } = useParams<RouteComponentParams>();
  const [loading, setLoading] = useState(true);
  const [useBillTo, setUseBillTo] = useState<boolean>(true);
  const [quotes, setQuotes] = useState<QuoteHistoryViewModel[]>([]);

  const refresh = useCallback(() => {
    setLoading(true);
    const payload = {
      UseBillToCustomer: useBillTo
    }
    fetchApi(`/api/Customer/CustomerQuoteHistory/${customerIdParam}`, payload, 'POST')
      .then((data: ViewModel) => {
        setQuotes(data.Quotes);
      })
      .catch(e => {
        // If not problem details
        if (!e?.status) alert('Unable to load customer quote history.');
      })
      .finally(() => setLoading(false));
  }, [customerIdParam, useBillTo]);

  useEffect(() => {
    refresh();
  }, [customerIdParam, refresh]);

  return (<>
    <CustomerNavBar id={customerIdParam} currentTab="QuoteHistory" />
    <Title string="Customer Quote History" />
    <br />
    <Grid
      data={quotes}
      detail={(props) => <QuoteHistoryDetail {...props} customerId={customerIdParam} useBillTo={useBillTo} />}
      dataItemKey="YYYYMM"
      showLoader={loading}
    >
      <GridToolbar>
        <ButtonGroup>
          <Button togglable
            selected={useBillTo}
            onClick={() => setUseBillTo(!useBillTo)}
          >
            Billing
          </Button>
          <Button togglable
            selected={!useBillTo}
            onClick={() => setUseBillTo(!useBillTo)}
          >
            Authorization
          </Button>
        </ButtonGroup>
      </GridToolbar>
      <GridNoRecords>No Customer Quote History Data.</GridNoRecords>
      <Column field="YYYYMM" title="YYYY-MM" />
      <Column field="HistoryCount" title="Quote Count" format="{0:n0}" />
      <Column field="BidCount" title="Bid Count" format="{0:n0}" />
      <Column field="DeclineCount" title="Decline Count" format="{0:n0}" />
      <Column field="OrderCount" title="Order Count" format="{0:n0}" />
      <Column title="Conversion %"
        cells={{ data: (props) => {
          const { HistoryCount, OrderCount } = props.dataItem;
          return <td {...props.tdProps}>{formatNumber(OrderCount / HistoryCount, 'p1')}</td>;
        } }}
      />
      <Column field="TotalCharges" title="Quote Total" format="{0:c2}" />
    </Grid>
  </>);
}

export default CustomerQuoteHistory;
